import { useRoute } from '#imports'
import { ref, watch } from 'vue'
import type { LocationQueryValue } from 'vue-router'

import type { Grade } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from '../Filters.translations'

const GRADE_ORDER: Grade['name'][] = [
  'PREMIUM',
  'FLAWLESS',
  'VERY_GOOD',
  'FAIR',
] as const

function sortGradesByOrder(grades: (Grade['name'] | null)[]): Grade['name'][] {
  return grades
    .filter((grade) => grade !== null)
    .sort((a, b) => {
      const indexA = GRADE_ORDER.indexOf(a)
      const indexB = GRADE_ORDER.indexOf(b)

      if (indexA !== -1 && indexB !== -1) return indexA - indexB
      if (indexA !== -1) return -1
      if (indexB !== -1) return 1

      return 0
    })
}

function computeGradeQuery(
  gradeQuery: LocationQueryValue | LocationQueryValue[],
  selectedGrade: Grade['name'],
) {
  if (typeof gradeQuery === 'string') {
    if (gradeQuery.includes(selectedGrade)) return undefined

    return sortGradesByOrder([gradeQuery, selectedGrade])
  }

  if (Array.isArray(gradeQuery)) {
    if (gradeQuery.includes(selectedGrade)) {
      const filteredGrades = gradeQuery.filter((g) => g !== selectedGrade)

      return filteredGrades.length > 0
        ? sortGradesByOrder(filteredGrades)
        : undefined
    }

    return sortGradesByOrder([...gradeQuery, selectedGrade])
  }

  return selectedGrade
}

export function useGradeFilter() {
  const i18n = useI18n()
  const route = useRoute()

  const selectedFilterByGrade = ref<LocationQueryValue | LocationQueryValue[]>(
    [],
  )
  const gradeList: { label: string; value: Grade['name'] }[] = [
    {
      label: i18n(translations.filterByGradePremium),
      value: 'PREMIUM',
    },
    {
      label: i18n(translations.filterByGradeFlawless),
      value: 'FLAWLESS',
    },
    {
      label: i18n(translations.filterByGradeGood),
      value: 'VERY_GOOD',
    },
    {
      label: i18n(translations.filterByGradeFair),
      value: 'ECO',
    },
  ]

  watch(
    () => route.query,
    () => {
      const { backbox_grade_label__in: grade } = route.query

      if (!grade) {
        selectedFilterByGrade.value = []

        return
      }

      if (typeof grade === 'string') selectedFilterByGrade.value = [grade]
      if (Array.isArray(grade)) selectedFilterByGrade.value = grade
    },
    { immediate: true },
  )

  return {
    computeGradeQuery,
    gradeList,
    selectedFilterByGrade,
  }
}
